import React, { useEffect, useState } from "react"
import { useParams, Outlet, Navigate } from "react-router-dom"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { useSession } from "@/features/auth/context/session.context"
import { authFetchJson } from "@/shared/lib/auth-fetch"

/**
 * Region layout — SPA architecture.
 *
 * Forces a remount on region change using the region slug from route params.
 * Validates the region slug and redirects if the region is invalid.
 */
function RegionLayout() {
  const { region } = useParams()
  const { regionId, isLoading } = useRegionId()
  const { profile, isLoading: sessionLoading } = useSession()
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(null)
  const [isResolvingSlug, setIsResolvingSlug] = useState(false)

  useEffect(() => {
    if (isLoading || sessionLoading) return
    if (regionId !== null) return // Valid region slug

    // Invalid region slug - resolve a fallback slug
    setIsResolvingSlug(true)
    const fetchFallbackSlug = async () => {
      try {
        const selectedRegionId = profile?.selectedRegion ?? 2 // Default region ID (Florida)
        const res = await authFetchJson<{ slug: string }>(`/regions/${selectedRegionId}`)
        if (res.success && res.data?.slug) {
          setResolvedSlug(res.data.slug)
        } else {
          setResolvedSlug("florida")
        }
      } catch {
        setResolvedSlug("florida")
      } finally {
        setIsResolvingSlug(false)
      }
    }
    fetchFallbackSlug()
  }, [regionId, isLoading, sessionLoading, profile])

  if (isLoading || sessionLoading || isResolvingSlug) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (regionId === null) {
    const currentPath = window.location.pathname
    const currentSearch = window.location.search
    const segments = currentPath.split("/").filter(Boolean)
    // Replace the first segment (invalid region slug) with the resolved fallback slug
    segments[0] = resolvedSlug || "florida"
    const targetPath = `/${segments.join("/")}${currentSearch}`

    return <Navigate to={targetPath} replace />
  }

  return <React.Fragment key={region}><Outlet /></React.Fragment>
}

export default RegionLayout
